import _words from 'lodash/words';
import _filter from 'lodash/filter';
import _kebabCase from 'lodash/kebabCase';
import _get from 'lodash/get';
import _capitalize2 from 'lodash/capitalize';
import React from 'react';
import { CURRENCY_SYMBOL_MAP, money, formatZipcode } from '@zillow/formatters';

var EMPTY_VALUE_STRING = '--';
var ONE_MILLION_PRETTY_LOWER_BOUND = 999499;
var EXPECTED_CITY_URL_TEXT = /^Homes in (.*)$/;
var EXPECTED_BOROUGH_URL_TEXT = /^Homes in (.*)$/;
var RID_TEXT = /(.*)_rid(.*)/;
var LOT_ACRE_THRESHOLD = 10890; // 1 sqft =  2.29568411e-5 acres

var ONE_SQUARE_FEET = 2.29568411e-5;
var ZILLOW_BRAND_WORDS = new Set(['Zillow', 'Zestimate', 'zillow', 'zestimate']);
var CONTACT_MANAGER_STRING = 'Contact manager';
var ZILLOW_BRAND_PHRASES = [{
  matcher: /Make me move/gi,
  // note case-insensitive matcher
  replacement: 'Make Me Move'
}];
var RESO_FACT_LABELS = {
  virtualTour: 'Virtual tour',
  leaseAmount: 'Lease amount',
  annualTaxAmount: 'Annual tax amount',
  hoaFee1: 'HOA fee',
  hoaFee2: 'Second HOA fee'
}; // Takes a function and returns a function
//
// The passed function will be applied to x if x is truthy, otherwise null will be returned.
// Designed to wrap JSX props so that they will only be passed if they exist. This is because
// a null prop will be ignored.

var safe = function safe(fn, fallback) {
  if (fallback === void 0) {
    fallback = null;
  }

  return function (x) {
    return x ? fn(x) : fallback;
  };
};
/**
 * Pluralizes given word if required by the given count
 *
 * @param count - number of items being referred to
 * @param singular - the singular form of the word. if no plural is passed, 's' will be added
 * @param plural - the plural form of the word. used if count is not 1
 * // TODO from static-mortgage
 */


function pluralize(count, singular, plural) {
  if (count === 1) {
    return singular;
  }

  if (plural) {
    return plural;
  }

  return singular + "s";
}
/**
 * Internal function that is used by "capitalize" and "capitalizeFirst"
 * functions. Exclude roman numerals from the standard capitalization practice.
 * Also ensures that brand-related words do not get lower-cased when run through
 * either of the above functions. Everything else is run through lodash's
 * capitalization routine.
 */

var romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
var directions = ['sw', 'nw', 'se', 'ne'];

var capitalizeWord = function capitalizeWord(word) {
  if (word.startsWith('(')) {
    return "(" + capitalizeWord(word.substring(1));
  }

  if (!Number.isNaN(Number(word))) {
    // return numbers in the string, like an address. Ex:
    // 123 cherry lane
    return word;
  }

  if (ZILLOW_BRAND_WORDS.has(word)) {
    return _capitalize2(word);
  }

  if (romanNumerals.indexOf(word.toLowerCase()) >= 0) {
    return word.toUpperCase();
  } // capitalize both letters of directions such as SE


  if (directions.includes(word.toLowerCase())) {
    return word.toUpperCase();
  }

  return _capitalize2(word);
};
/**
 * Capitalizes each word in the given input, ignoring roman numerals.
 */


var capitalize = function capitalize(input) {
  if (!input) {
    return '';
  }

  return input.trim().split(' ').map(capitalizeWord).join(' ');
};
/*
 * Capitalizes the first word in a string. Useful for making strings "sentence case"
 * for double-scroll layout. Ex: "123 cherry lane" becomes "123 Cherry lane", or
 * "Facts and Features" becomes "Facts and features". If the result string contains matches to
 * a brand phrase e.g. 'Make Me Move' then it will replace those instances with the correct casing.
 */

var capitalizeFirst = function capitalizeFirst(input) {
  var firstCapitalizedFound = false;

  if (!input || input && typeof input !== 'string') {
    return '';
  }

  var capFirst = function capFirst(word) {
    if (ZILLOW_BRAND_WORDS.has(word)) {
      return _capitalize2(word);
    }

    if (firstCapitalizedFound) {
      // the first word has already been found at this point and capitalized, so
      // everything from here on out can be lower case.
      return word.toLowerCase();
    }

    if (!firstCapitalizedFound && !Number.isNaN(Number(word))) {
      // return numbers in the string, like an address. Ex:
      // 123 cherry lane
      return word;
    }

    if (!firstCapitalizedFound) {
      // the first non-number string was found, so capitalize it, and toggle the flag.
      firstCapitalizedFound = true;
      return capitalizeWord(word);
    }

    return word;
  };

  var lowerCasedInput = input.trim().split(' ').map(capFirst).join(' ');
  ZILLOW_BRAND_PHRASES.forEach(function (_ref) {
    var matcher = _ref.matcher,
        replacement = _ref.replacement;
    lowerCasedInput = lowerCasedInput.replace(matcher, replacement);
  });
  return lowerCasedInput;
};
var priceWithCurrency = function priceWithCurrency(currency, showCents) {
  if (currency === void 0) {
    currency = '$';
  }

  return safe(function (p) {
    return money(p, currency, showCents);
  }, "" + (CURRENCY_SYMBOL_MAP[currency] || currency) + EMPTY_VALUE_STRING);
};
var price = priceWithCurrency('$');
var photos = safe(function (p) {
  return p + " " + pluralize(p, 'photo');
}, '');
var beds = safe(function (b) {
  return b + " " + pluralize(b, 'bed');
}, EMPTY_VALUE_STRING + " beds");
var bds = safe(function (b) {
  return b + " " + pluralize(b, 'bd');
}, EMPTY_VALUE_STRING + " bds");
var baths = safe(function (b) {
  return b + " " + pluralize(b, 'bath');
}, EMPTY_VALUE_STRING + " baths");
var ba = safe(function (b) {
  return b + " ba";
}, EMPTY_VALUE_STRING + " ba");
var livingArea = safe(function (l) {
  return l.toLocaleString() + " sqft";
}, EMPTY_VALUE_STRING + " sqft");
var streetFormat = safe(function (c) {
  return capitalize(c);
}, EMPTY_VALUE_STRING);
var cityFormat = safe(function (c) {
  return capitalize(c);
}, EMPTY_VALUE_STRING);
var stateFormat = safe(function (s) {
  return s.toUpperCase();
}, EMPTY_VALUE_STRING);
var zipcodeFormat = safe(function (z) {
  return formatZipcode(z);
}, EMPTY_VALUE_STRING); // same as bds, but return 'Studio' when beds is explicitly set to 0 (not simply falsey)

function bdsWithStudio(count) {
  if (count === 0) {
    return 'Studio';
  }

  return bds(count);
} // same as beds, but return 'Studio' when beds is explicitly set to 0 (not simply falsey)

function bedsWithStudio(count) {
  if (count === 0) {
    return 'Studio';
  }

  return beds(count);
}
var livingAreaWithSeparateValueUnit = function livingAreaWithSeparateValueUnit(la) {
  var formattedLivingArea = EMPTY_VALUE_STRING;
  var unit = 'sqft';

  if (la && typeof la === 'number' && la > 0) {
    if (la >= LOT_ACRE_THRESHOLD) {
      var acres = la * ONE_SQUARE_FEET; // Use toLocaleString to format number

      var acresWithMaxTwoDecimal = Math.round(acres * 100) / 100;
      formattedLivingArea = acresWithMaxTwoDecimal.toLocaleString();
      unit = pluralize(acresWithMaxTwoDecimal, 'acre');
    } else {
      // Use toLocaleString to format number
      formattedLivingArea = la.toLocaleString();
    }
  }

  return {
    value: formattedLivingArea,
    unit: unit
  };
};
var formatLotSize = function formatLotSize(lotSize) {
  var formattedLot = EMPTY_VALUE_STRING + " sqft";

  if (lotSize && lotSize > 0) {
    if (lotSize >= LOT_ACRE_THRESHOLD) {
      var acres = lotSize * ONE_SQUARE_FEET; // Use toLocaleString to format number

      var acresWithMaxTwoDecimal = Math.round(acres * 100) / 100;
      formattedLot = acresWithMaxTwoDecimal.toLocaleString() + " " + pluralize(acresWithMaxTwoDecimal, 'acre');
    } else {
      // Use toLocaleString to format number
      formattedLot = lotSize.toLocaleString() + " sqft";
    }
  }

  return formattedLot;
};
var formatLotAreaOrLivingAreaWithFallback = function formatLotAreaOrLivingAreaWithFallback(value, unit, fallbackValue) {
  return value ? value.toLocaleString() + " " + (unit || 'sqft') : fallbackValue;
};
var formRegionIdLink = function formRegionIdLink(prefix, regionId) {
  var link = '';

  if (prefix && regionId) {
    link = "" + prefix + regionId + "_rid/";
  } else if (prefix) {
    link = prefix;
  } else if (regionId) {
    link = "/" + regionId + "_rid/";
  }

  return link;
};
var streetCityStateZip = function streetCityStateZip(st, c, s, z) {
  return streetFormat(st) + ", " + cityFormat(c) + ", " + stateFormat(s) + " " + zipcodeFormat(z);
};
var cityStateZip = function cityStateZip(c, s, z) {
  return cityFormat(c) + ", " + stateFormat(s) + " " + zipcodeFormat(z);
};
var stateZip = function stateZip(s, z) {
  return stateFormat(s) + " " + zipcodeFormat(z);
};
var stateLink = function stateLink(state, stateId) {
  var strState = state ? state.trim() : null;
  var prefix = strState ? "/" + strState.toLowerCase() + "/" : null;
  return formRegionIdLink(prefix, stateId);
};
var cityLink = function cityLink(city, state, cityId) {
  var strLink = _filter([_kebabCase(city && city.toLowerCase()), state && state.trim()]).join('-').toLowerCase();

  var prefix = strLink ? "/" + strLink + "/" : null;
  return formRegionIdLink(prefix, cityId);
};
var zipLink = function zipLink(zip, state, zipcodeId) {
  var strLink = _filter([state && state.trim(), zip ? zip.toString().replace(/ /g, '').trim() : null]).join('-').toLowerCase();

  var prefix = strLink ? "/" + strLink + "/" : null;
  return formRegionIdLink(prefix, zipcodeId);
};
var cityStateZipLink = function cityStateZipLink(city, state, zip) {
  var strLink = _filter([_kebabCase(city && city.toLowerCase()), state && state.trim(), zip ? zip.toString().replace(/ /g, '').trim() : null]).join('-').toLowerCase();

  return strLink && "/" + strLink + "/";
};
var communityLink = function communityLink(masterCommunity, subdivision, providerListingId) {
  // See CommunityURLFormatter.formatCommunityUrl for Java version
  // https://bitbucket.zgtools.net/projects/libs/repos/link-factories/browse/impl/src/main/java/com/zillow/web/links/community/CommunityURLFormatter.java
  var mCommunity = masterCommunity;
  var community = subdivision;

  if (!providerListingId) {
    return null;
  }

  if (!community) {
    community = 'Error Subdivision Name Unavailable';
  }

  if (!mCommunity) {
    mCommunity = '';
  }

  if (!String.prototype.normalize) {
    // eslint-disable-next-line no-unused-vars, no-extend-native
    String.prototype.normalize = function (form) {
      var accents = 'ÀÁÂÃÄÅàáâãäåßÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž';
      var accentsOut = 'AAAAAAaaaaaaBOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz';
      var stripped = this.split('');
      stripped.forEach(function (letter, index) {
        var i = accents.indexOf(letter);

        if (i !== -1) {
          stripped[index] = accentsOut[i];
        }
      });
      return stripped.join('');
    };
  }

  mCommunity = mCommunity.normalize('NFD').replace(/[^a-zA-Z0-9 ]/g, '');
  community = community.normalize('NFD').replace(/[^a-zA-Z0-9 ]/g, '');

  var strLink = _filter(_words(mCommunity && mCommunity.toLowerCase()).concat(_words(community && community.toLowerCase())), function (word) {
    return word !== 'the';
  }).join('-');

  return "/community/" + strLink + "/" + providerListingId + "_plid/";
};
var neighborhoodLink = function neighborhoodLink(neighborhood, city, state, zip, neighborhoodId) {
  var strLink = _filter([_kebabCase(neighborhood && neighborhood.toLowerCase()), _kebabCase(city && city.toLowerCase()), state && state.trim(), zip ? zip.toString().trim() : null]).join('-').toLowerCase();

  var prefix = strLink ? "/" + strLink + "/" : null;
  return formRegionIdLink(prefix, neighborhoodId);
};
var mortgageRatesLink = function mortgageRatesLink(city, state) {
  var strLink = _filter([state && state.trim(), _kebabCase(city && city.toLowerCase())]).join('/').toLowerCase();

  return strLink ? "/mortgage-rates/" + strLink + "/" : '/mortgage-rates/';
};
var refinanceLink = function refinanceLink(city, state) {
  var strLink = _filter([state && state.trim(), _kebabCase(city && city.toLowerCase())]).join('/').toLowerCase();

  return strLink ? "/refinance/" + strLink + "/" : '/refinance/';
};
var realEstateAgentsLink = function realEstateAgentsLink(city, state) {
  var strLink = cityLink(city, state);
  return strLink ? "/directory" + strLink + "real-estate-agents/" : '/directory/real-estate-agents/';
};
var getUrlPrefixForHomeStatus = function getUrlPrefixForHomeStatus(homeStatus) {
  if (homeStatus === 'FOR_RENT') {
    return '/homes/for_rent';
  }

  return '';
};
var getUrlSuffixForHomeStatus = function getUrlSuffixForHomeStatus(homeStatus, homeType) {
  if (homeStatus === 'FOR_RENT') {
    if (homeType === 'SINGLE_FAMILY') {
      return '/rent-houses/';
    }

    if (homeType === 'APARTMENT' || homeType === 'CONDO' || homeType === 'MULTI_FAMILY') {
      return '/apartments/';
    }

    if (homeType === 'TOWNHOUSE') {
      return '/rent-townhomes/';
    }
  }

  return '';
}; // Logic borrowed from here (but does not match exactly, close enough though):
// eslint-disable-next-line
// https://opengrok.in.zillow.net/source/xref/zillow-util.LIBS/src/main/java/com/zillow/util/format/BigCurrencyFormat.java

var formatCurrency = function formatCurrency(num, currency) {
  if (currency === void 0) {
    currency = '$';
  }

  var v;
  var s;
  var n = parseInt(num, 10); // currency should be a valid non-negative number

  if (Number.isNaN(n) || n < 0) {
    return null;
  }

  if (n >= 1000000000) {
    v = Math.round(n / 100000000) / 10;
    s = 'B';
  } else if (n >= 1000000) {
    v = Math.round(n / 100000) / 10;
    s = 'M';
  } else if (n > ONE_MILLION_PRETTY_LOWER_BOUND && n < 1000000) {
    v = 1;
    s = 'M';
  } else if (n >= 1000) {
    v = Math.round(n / 100) / 10;
    s = 'K';
  } else {
    v = Math.round(n);
    s = '';
  }

  return "" + (CURRENCY_SYMBOL_MAP[currency] || currency) + v + s;
}; // We need to user agent sniff for the delimeter.
// eslint-disable-next-line
// See: http://stackoverflow.com/questions/6480462/how-to-pre-populate-the-sms-body-text-via-an-html-link

var isFromIos = function isFromIos() {
  if (typeof window === 'undefined') {
    return false;
  }

  var userAgent = _get(window, 'navigator.userAgent', '');

  return /(iPad|iPhone|iPod)/.test(userAgent);
};

var getIosversion = function getIosversion() {
  if (isFromIos()) {
    var userAgent = _get(window, 'navigator.userAgent', '');

    var v = userAgent.match(/OS (\d+)_?/);
    return parseInt(v[1], 10);
  }

  return null;
};

var generateTextMessageHref = function generateTextMessageHref(bodyMessage, hdpUrl) {
  var textMessageHref = null;

  if (typeof window !== 'undefined') {
    var delimeter = '?';

    if (isFromIos()) {
      var iOSversion = getIosversion();

      if (iOSversion < 8) {
        delimeter = ';';
      } else {
        delimeter = '&';
      }
    }

    var origin = window.location.protocol + "//" + window.location.hostname;
    var href = hdpUrl ? "" + origin + hdpUrl : window.location.href;
    var body = bodyMessage + " " + href + "?utm_source=txtshare";
    textMessageHref = "sms:" + delimeter + "body=" + encodeURIComponent(body);
  }

  return textMessageHref;
};
var extractAndFormatCityName = function extractAndFormatCityName(property) {
  var cityUrlText = _get(property, 'citySearchUrl.text', null);

  var formattedCity = null; // We should first try to extract the city name from the cityUrlText.text

  if (cityUrlText) {
    var match = EXPECTED_CITY_URL_TEXT.exec(cityUrlText);

    if (match && match[1]) {
      formattedCity = match[1];
    }
  } // If for some reason extraction above was not possible, use the city
  // field from the property itself.


  if (!formattedCity && property && property.city) {
    formattedCity = capitalize(property.city);
  }

  return formattedCity;
};
var formatBoroughName = function formatBoroughName(boroughText) {
  var boroughName = ''; // the name of the borough starts on index 9 in boroughText
  // boroughText is in the format "homes in <borough>"

  var match = EXPECTED_BOROUGH_URL_TEXT.exec(boroughText);

  if (match && match[1]) {
    boroughName = match[1];
    return boroughName;
  }

  return null;
};
var doesUrlContainRegionId = function doesUrlContainRegionId(url) {
  var contains = false;

  if (url) {
    var match = RID_TEXT.exec(url);

    if (match && match[1]) {
      contains = true;
    }
  }

  return contains;
};
/**
 * Removes accents from a string.
 * **NOTE: Does not work with IE11**
 * @param {string} str - String to strip accents from
 * @returns {string} string without accents
 */

var stripAccents = function stripAccents(str) {
  if (!String.prototype.normalize) {
    return str;
  }

  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
/**
 * Converts a string to a valid slug by removing special characters and spaces
 * @param {string} str - String to convert into a slug
 * @returns {string} slugified string
 */

var getSlug = function getSlug(str) {
  return stripAccents(str.toLowerCase()).trim() // Remove invalid chars
  .replace(/[^a-z0-9 -]/g, '') // Collapse whitespace and replace by -
  .replace(/\s+/g, '-') // Collapse dashes
  .replace(/-+/g, '-') // Remove dashes at end of line
  .replace(/-$/, '') // Remove dashes at start of line
  .replace(/^-/, '');
};

var needsProtocolPrefix = function needsProtocolPrefix(virtualTourUrl) {
  return virtualTourUrl && !virtualTourUrl.startsWith('http://') && !virtualTourUrl.startsWith('https://') && !virtualTourUrl.startsWith('//');
};

var constructVirtualTourHref = function constructVirtualTourHref(url) {
  return needsProtocolPrefix(url) ? "//" + url : url;
};
var formatFactValue = function formatFactValue(_ref2) {
  var factLabel = _ref2.factLabel,
      factValue = _ref2.factValue,
      currency = _ref2.currency,
      isForRent = _ref2.isForRent,
      _ref2$trackEventConfi = _ref2.trackEventConfig,
      trackEventConfig = _ref2$trackEventConfi === void 0 ? {} : _ref2$trackEventConfi;

  if (factValue === null || factValue === undefined) {
    return null;
  }

  if (typeof factValue === 'boolean') {
    return factValue ? 'Yes' : 'No';
  }

  if (Array.isArray(factValue)) {
    if (isForRent && factValue.includes('None')) {
      return CONTACT_MANAGER_STRING;
    }

    return factValue.join(', ');
  }

  if (isForRent && /None/i.test(factValue)) {
    return CONTACT_MANAGER_STRING;
  }

  if (factLabel === RESO_FACT_LABELS.virtualTour) {
    return /*#__PURE__*/React.createElement("a", {
      href: constructVirtualTourHref(factValue),
      target: "_blank",
      rel: "nofollow noopener noreferrer",
      onClick: trackEventConfig.VIRTUAL_TOUR
    }, "View virtual tour");
  } // never matches virtualTour label - already checked by condition above


  if (Object.values(RESO_FACT_LABELS).includes(factLabel)) {
    return money(factValue, currency);
  } // factValue could be a string, number of bathrooms, etc.


  return factValue;
};

var parkingDescription = function parkingDescription(spaceCount, parkingType) {
  if (spaceCount === 1) {
    return spaceCount + " " + parkingType + " space";
  }

  if (spaceCount > 1) {
    return spaceCount + " " + parkingType + " spaces";
  }

  return parkingType;
};

var formatArray = function formatArray(arr) {
  if (!arr || arr.length === 0) {
    return null;
  }

  return arr.join(', ');
};

var carportParkingFact = function carportParkingFact(_ref3) {
  var factLabel = _ref3.factLabel,
      _ref3$parkingData = _ref3.parkingData,
      hasCarport = _ref3$parkingData.hasCarport,
      carportSpaces = _ref3$parkingData.carportSpaces,
      coveredSpaces = _ref3$parkingData.coveredSpaces,
      parking = _ref3$parkingData.parking;

  if (!hasCarport) {
    return null;
  }

  var spaceCount = carportSpaces || coveredSpaces || parking;
  var factValue = parkingDescription(spaceCount, 'Carport');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};

var basicParkingFact = function basicParkingFact(_ref4) {
  var factLabel = _ref4.factLabel,
      parking = _ref4.parkingData.parking;

  if (!parking) {
    return null;
  }

  var factValue = parkingDescription(parking, 'Parking');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};

var openParkingFact = function openParkingFact(_ref5) {
  var factLabel = _ref5.factLabel,
      _ref5$parkingData = _ref5.parkingData,
      hasOpenParking = _ref5$parkingData.hasOpenParking,
      openParkingSpaces = _ref5$parkingData.openParkingSpaces,
      parking = _ref5$parkingData.parking;

  if (!hasOpenParking) {
    return null;
  }

  var spaceCount = openParkingSpaces || parking;
  var factValue = parkingDescription(spaceCount, 'Open Parking');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};

var noParkingDataFact = function noParkingDataFact(_ref6) {
  var factLabel = _ref6.factLabel;
  return {
    factLabel: factLabel,
    factValue: 'No Data'
  };
};

var garageParkingFact = function garageParkingFact(_ref7) {
  var factLabel = _ref7.factLabel,
      _ref7$parkingData = _ref7.parkingData,
      hasGarage = _ref7$parkingData.hasGarage,
      garageSpaces = _ref7$parkingData.garageSpaces,
      coveredSpaces = _ref7$parkingData.coveredSpaces,
      parking = _ref7$parkingData.parking;

  if (!hasGarage) {
    return null;
  }

  var spaceCount = garageSpaces || coveredSpaces || parking;
  var factValue = parkingDescription(spaceCount, 'Garage');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};
var attachedGarageParkingFact = function attachedGarageParkingFact(_ref8) {
  var factLabel = _ref8.factLabel,
      _ref8$parkingData = _ref8.parkingData,
      hasAttachedGarage = _ref8$parkingData.hasAttachedGarage,
      garageSpaces = _ref8$parkingData.garageSpaces,
      coveredSpaces = _ref8$parkingData.coveredSpaces,
      parking = _ref8$parkingData.parking;

  if (!hasAttachedGarage) {
    return null;
  }

  var spaceCount = garageSpaces || coveredSpaces || parking;
  var factValue = parkingDescription(spaceCount, 'Attached Garage');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};
var coveredParkingFact = function coveredParkingFact(_ref9) {
  var factLabel = _ref9.factLabel,
      coveredSpaces = _ref9.parkingData.coveredSpaces;

  if (!coveredSpaces) {
    return null;
  }

  var factValue = parkingDescription(coveredSpaces, 'Covered Parking');
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};
var parkingFeaturesFact = function parkingFeaturesFact(_ref10) {
  var factLabel = _ref10.factLabel,
      parkingFeatures = _ref10.parkingData.parkingFeatures;

  if (!parkingFeatures) {
    return null;
  }

  var factValue = formatArray(parkingFeatures);
  return {
    factLabel: factLabel,
    factValue: factValue
  };
};
var atAGlanceFactsParking = function atAGlanceFactsParking(_ref11) {
  var factLabel = _ref11.factLabel,
      data = _ref11.data;
  var parkingData = {
    hasAttachedGarage: data.hasAttachedGarage,
    hasGarage: data.hasGarage,
    hasCarport: data.hasCarport,
    hasOpenParking: data.hasOpenParking,
    garageSpaces: data.garageSpaces,
    coveredSpaces: data.coveredSpaces,
    carportSpaces: data.carportSpaces,
    parking: data.parking,
    openParkingSpaces: data.openParkingSpaces,
    parkingFeatures: data.parkingFeatures
  };
  return attachedGarageParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || garageParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || carportParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || coveredParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || openParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || basicParkingFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || parkingFeaturesFact({
    factLabel: factLabel,
    parkingData: parkingData
  }) || noParkingDataFact({
    factLabel: factLabel
  });
};

export { CONTACT_MANAGER_STRING, RESO_FACT_LABELS, atAGlanceFactsParking, attachedGarageParkingFact, ba, baths, bds, bdsWithStudio, beds, bedsWithStudio, capitalize, capitalizeFirst, cityFormat, cityLink, cityStateZip, cityStateZipLink, communityLink, constructVirtualTourHref, coveredParkingFact, doesUrlContainRegionId, extractAndFormatCityName, formRegionIdLink, formatBoroughName, formatCurrency, formatFactValue, formatLotAreaOrLivingAreaWithFallback, formatLotSize, garageParkingFact, generateTextMessageHref, getSlug, getUrlPrefixForHomeStatus, getUrlSuffixForHomeStatus, livingArea, livingAreaWithSeparateValueUnit, mortgageRatesLink, neighborhoodLink, parkingFeaturesFact, photos, pluralize, price, priceWithCurrency, realEstateAgentsLink, refinanceLink, stateFormat, stateLink, stateZip, streetCityStateZip, streetFormat, stripAccents, zipLink, zipcodeFormat };
